import { useCallback, useMemo } from "react";

import { usePostHog } from "posthog-js/react";

import {
  useAPI,
  useAuthContext,
  useCarrierShipmentPageContext,
  useShipmentQuotes,
} from "hooks";

import { SubmitOrCancelQuote } from "./SubmitOrCancelQuote";
import { ClientAppEvent } from "../../../../constants";

interface Props {
  shipment: CondensedShipment | undefined;
}

export function SubmitOrCancelQuoteContainer(props: Props) {
  const { shipment } = props;

  const { api } = useAPI();
  const { user } = useAuthContext();
  const { onRefresh } = useCarrierShipmentPageContext();
  const { quotes, isLoading } = useShipmentQuotes(shipment?.info.id ?? 0);
  const posthog = usePostHog();

  const findCarrierActiveShipment = useCallback(
    (q: CondensedQuote) =>
      q.internal_state === "ACTIVE" && q.created_by.id === user?.id,
    [user?.id],
  );

  const canSubmitQuote = useMemo(
    () => !quotes.some((q) => findCarrierActiveShipment(q)),
    [quotes, findCarrierActiveShipment],
  );

  const cancelLatestActiveQuote = () => {
    const activeQuote = quotes.find((q) => findCarrierActiveShipment(q));
    if (activeQuote) {
      api("post", `/quotes/${activeQuote?.quote}/cancel`).then(() => {
        posthog?.capture(ClientAppEvent.QUOTE_CANCELLED, {
          quote_id: activeQuote.quote,
          shipment_id: shipment?.info.id,
        });
        if (onRefresh != null) {
          onRefresh();
        }
      });
    }
  };

  return (
    <SubmitOrCancelQuote
      isLoading={isLoading}
      shipment={shipment}
      canSubmitQuote={canSubmitQuote}
      onCancel={cancelLatestActiveQuote}
    />
  );
}
